import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { tokenStore } from './storage/TokenStore';

const apiUrl = process.env.NEXT_PUBLIC_API_URL as string;

const PUBLIC_PATHS = ['/login', '/register', '/forgot-password'];
let isHandlingAuthFailure = false;

const isOnPublicPath = () => {
  if (typeof window === 'undefined') return true;
  return PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
};

const handleAuthFailure = () => {
  if (typeof window === 'undefined' || isHandlingAuthFailure) return;
  if (isOnPublicPath()) return;
  isHandlingAuthFailure = true;
  tokenStore.clearTokens();
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?redirect=${redirect}`;
};

// Add type augmentation for custom config
declare module 'axios' {
  export interface AxiosRequestConfig {
    withStraticsUiHeader?: boolean;
  }
}

// Send firebase access token to each subsequent API call
const attachAccessToken = (
  request: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const appAccessToken = tokenStore.getAppAccessToken();
  const firebaseAccessToken = tokenStore.getFirebaseAccessToken();
  let tokenToUse = '';
  if (appAccessToken) {
    tokenToUse = `JWT ${appAccessToken}`;
  } else if (firebaseAccessToken) {
    tokenToUse = `Bearer ${firebaseAccessToken}`;
  }

  if (request.headers) {
    request.headers.Authorization = tokenToUse;
    if (request.withStraticsUiHeader) {
      request.headers['Stratics-Ui'] = '1';
    }
  } else {
    // Ensure headers are initialized
    request.headers = request.headers || new AxiosHeaders();
    request.headers.set('Authorization', tokenToUse);
    if (request.withStraticsUiHeader) {
      request.headers.set('Stratics-Ui', '1');
    }
  }
  return request;
};

// Type for common API error response patterns
type ApiErrorResponse =
  | {
      Error?: string;
      error?: string;
      message?: string;
      detail?: string;
    }
  | string;

// Response interceptor to handle errors and extract error messages
const handleResponseError = (error: AxiosError): Promise<never> => {
  // On auth failure, clear tokens and bounce to login.
  // The platform polls stats every 10s; an overnight token expiry was leaving the
  // app in a broken state instead of recovering. See issue #190.
  if (error.response?.status === 401 || error.response?.status === 403) {
    handleAuthFailure();
  }

  if (error.response?.data) {
    // Try to extract error message from response data
    const responseData = error.response.data as ApiErrorResponse;

    // Debug: log the response data to see what we're getting
    console.log('API Error Response Data:', responseData);

    // Check for common error message patterns
    let errorMessage = '';

    if (typeof responseData === 'string') {
      errorMessage = responseData;
    } else if (responseData.Error) {
      errorMessage = responseData.Error;
    } else if (responseData.error) {
      errorMessage = responseData.error;
    } else if (responseData.message) {
      errorMessage = responseData.message;
    } else if (responseData.detail) {
      errorMessage = responseData.detail;
    }

    if (errorMessage) {
      // Modify the original error message instead of creating a new error
      error.message = errorMessage;
      throw error;
    }
  }

  // If no specific error message found, fall back to the original error
  throw error;
};

const handleResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const api = axios.create({
  baseURL: apiUrl,
});

api.interceptors.request.use(attachAccessToken);
api.interceptors.response.use(handleResponse, handleResponseError);

export { api };
